/*
 * The held button: the one action of a screen at a moment it cannot be taken — a commit waiting for a
 * newer version, a Publish waiting for a review — kept where it is and saying why.
 *
 * A held button is never `disabled` and never removed. A disabled button drops out of the tab order
 * and answers a press with nothing, so a keyboard loses its place and a screen reader cannot hear
 * why the one thing the person came to do is not there. Held, the button stays reachable: it is
 * `aria-disabled`, described by the sentence already on screen that says what holds it, and a press
 * speaks the short reason as the answer to the act (a press that answers with silence reads as a
 * press that was lost). `onHeldPress` is for the step a reason points to — the focus to the box a
 * tick is waiting for — and nothing is ever done from a held button beyond that.
 *
 * `disabled` still means what it always means, for the moments there is nothing to say: while the
 * action runs, or before there is anything to act on. A held button ignores it.
 *
 * A chord that stands for the same act (`Mod+Enter` in a dialog) goes through `useHeldAnswer`, so the
 * key and the button can never answer differently.
 */

import { useCallback } from "react"
import type { AnyKey } from "../core/i18n/index.ts"
import { useAnnounce } from "../core/shell/Announcer.tsx"
import { Button, type PressProps } from "./Button.tsx"

/** What holds a button: the words a press speaks, and the element on screen that says why. */
export interface Held {
	/** The short reason, spoken as the answer to a press. */
	sentence: { key: AnyKey; args?: Record<string, string | number> }
	/** The id of the visible sentence that already says why; the button is described by it. */
	id: string
}

export interface HeldButtonProps extends Omit<
	PressProps,
	"label" | "onClick" | "aria-disabled" | "aria-describedby"
> {
	label: string
	/** What holds the button now, or null when it can be pressed. */
	held: Held | null
	onPress?: () => void
	/** Runs after the reason was spoken: the step the reason points to, never the action itself. */
	onHeldPress?: () => void
}

/** The answer a held button gives, for a chord that stands for the same act. */
export function useHeldAnswer(): (held: Held, then?: () => void) => void {
	const announce = useAnnounce()
	return useCallback(
		(held, then) => {
			announce(held.sentence.key, held.sentence.args ?? {}, { answersAnAction: true })
			then?.()
		},
		[announce],
	)
}

export function HeldButton({
	label,
	held,
	onPress,
	onHeldPress,
	disabled,
	...button
}: HeldButtonProps) {
	const answer = useHeldAnswer()
	if (held === null) {
		return <Button {...button} label={label} disabled={disabled} onClick={onPress} />
	}
	return (
		<Button
			{...button}
			label={label}
			aria-disabled={true}
			aria-describedby={held.id}
			onClick={() => {
				answer(held, onHeldPress)
			}}
		/>
	)
}
